'use client';

import { VStack, HStack } from '@terra/ui-styled-system';
import { BodyStrong, MetaDefault } from '../../../../../components/typography';
import { Button } from '../../../../../components/styled/button';
import type {
  PromptOption,
  HITLRequestConfirmationMessage,
  HITLRequestConfirmationDecision,
} from '@terra/shared-dto-chat-types';
import type {
  ToolCallGenericMessage,
  HITLActionRequest,
  HITLDecision,
} from '@terra/shared-dto-chat-types';
import {
  findOptionByValue,
  readPendingReviewTitle,
  readEditedActionArgs,
  isPortalReviewContentType,
  isSignalUpdateToolName,
  isStandaloneResolvedHITLCard,
  shouldHideResolvedHitlWhenToolResultExists,
  isExecutedCatalogToolResult,
  hasExecutedResultForActions,
  getResolvedDecisionCardStatus,
  readHitlToolFailureMessage,
} from '../hitl-card.utils';
import {
  Alert01Icon,
  Target03Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
} from '@terra/ui-icons';
import {
  useState,
  useRef,
  useMemo,
  createElement,
  useCallback,
  useEffect,
  Fragment,
  type KeyboardEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import type { FindingData, SignalData } from '../types';
import { MinimalToolCard } from '../../MinimalToolCard';
import {
  getToolConfig,
  defaultContentRenderer,
  defaultEditModalRenderer,
} from '../../tool-config';
import { getGuardrailReasonHeaderAction } from '../../tools/shared/guardrail-reason-header-action';
import { withElevatedBadge } from './HitlElevatedBadge';
import { HitlPendingActionButton } from './HitlPendingActionButton';
import { buildPendingApprovalSlots } from '../hitl-pending-approval-slots';
import { useAlwaysAllowRuleHandler } from '../terminal-approval-rules-context';
import type {
  OnOpenEditInMainContent,
  OnCloseEditInMainContent,
  OnOpenChatObject,
} from '../../hitl-types';
import { useHitlDecisionWindow } from '../hooks/useHitlDecisionWindow';
import { readPortalObjectResult } from '../../../utils/portal-object-result';
import { PendingObjectReviewCard } from './PendingObjectReviewCard';
import { HitlActionOutcomeRow } from './HitlActionOutcomeRow';
import { HitlRejectForm } from './HitlRejectForm';
import {
  renderInlineToolResultContent as renderInlineToolResultContentBase,
  renderInlineActionRequestContent as renderInlineActionRequestContentBase,
} from '../hitl-inline-action-content';
import {
  buildBatchDecision,
  getActionRequestKey,
  getActionRequestDecisionIds,
} from '../hitl-decision.utils';
import {
  useHitlDecisionDispatch,
  type OnHITLActionMessageEvent,
} from '../hooks/useHitlDecisionDispatch';
import {
  useHitlApprovalGate,
  useHitlMultiActionApprovalGate,
} from '../hooks/useHitlApprovalGate';
import { useResolvedSignalDisplayTitle } from '../../../hooks/useResolvedSignalDisplayTitle';

type HITLRequestConfirmationPromptCardProps = {
  selectedDecision?: HITLRequestConfirmationDecision;
  selectedDecisions?: HITLDecision[];
  dataMessage?: HITLRequestConfirmationMessage;
  messagePartId?: string;
  onHITLActionMessageEvent?: OnHITLActionMessageEvent;
  onOpenEditInMainContent?: OnOpenEditInMainContent;
  onCloseEditInMainContent?: OnCloseEditInMainContent;
  onOpenObject?: OnOpenChatObject;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
  hasToolResult?: boolean;
  toolResultsById?: Record<string, ToolCallGenericMessage>;
};
type ResolvedHITLRequestConfirmationDecision =
  NonNullable<HITLRequestConfirmationDecision>;

export function HITLRequestConfirmationPromptCard({
  dataMessage,
  messagePartId,
  selectedDecision,
  selectedDecisions,
  onHITLActionMessageEvent,
  onOpenEditInMainContent,
  onCloseEditInMainContent,
  onOpenObject,
  isLastMessage,
  isChatLoading,
  hasToolResult,
  toolResultsById,
}: HITLRequestConfirmationPromptCardProps) {
  const { getBackgroundExecutionByToolCallId, canDecide } =
    useHitlDecisionWindow({ dataMessage, isLastMessage });
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const hitlRequest = dataMessage?.hitlRequestConfirmationPrompt;
  const [isRejectInline, setIsRejectInline] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditingHttpInline, setIsEditingHttpInline] = useState(false);
  const [multiActionDecisions, setMultiActionDecisions] = useState<
    Record<string, HITLDecision>
  >({});
  const [rejectingActionKey, setRejectingActionKey] = useState<string | null>(
    null,
  );
  const [multiRejectMessage, setMultiRejectMessage] = useState('');
  const buildAlwaysAllowHandler = useAlwaysAllowRuleHandler();
  const actionRequests = useMemo(
    () => hitlRequest?.actionRequests ?? [],
    [hitlRequest?.actionRequests],
  );
  const multiActionGatedActions = useMemo(
    () =>
      actionRequests.flatMap((actionRequest, index) => {
        const actionConfig = getToolConfig(actionRequest.name);

        if (!actionConfig.resolveHitlApprovalBlocked) {
          return [];
        }

        const actionArgs =
          actionRequest.args &&
          typeof actionRequest.args === 'object' &&
          !Array.isArray(actionRequest.args)
            ? (actionRequest.args as Record<string, unknown>)
            : undefined;

        return [
          {
            key: getActionRequestKey(actionRequest, index),
            config: actionConfig,
            toolArgs: actionArgs,
          },
        ];
      }),
    [actionRequests],
  );
  const primaryActionRequest = actionRequests[0];
  const isMultiAction = actionRequests.length > 1;
  const toolName = primaryActionRequest?.name;
  const toolArgs = primaryActionRequest?.args as
    | Record<string, unknown>
    | undefined;
  const config = getToolConfig(toolName);
  const { isApprovalBlocked } = useHitlApprovalGate({ config, toolArgs });
  const { blockedActionKeys } = useHitlMultiActionApprovalGate({
    gatedActions: multiActionGatedActions,
  });
  const toolIcon = createElement(config.icon, { size: 12 });
  const hitlConfig = config.hitlConfig;
  const extractedData = useMemo(() => {
    if (!toolArgs || !hitlConfig?.extractData) return [];

    return hitlConfig.extractData(toolArgs);
  }, [toolArgs, hitlConfig]);
  const isSignalUpdateReview = isSignalUpdateToolName(toolName);
  const pendingReviewTitleFromArgs = readPendingReviewTitle({
    toolArgs,
    extractedData,
  });
  const resolvedUpdateSignalTitle = useResolvedSignalDisplayTitle({
    signalId:
      typeof toolArgs?.signalId === 'string' ? toolArgs.signalId : undefined,
    title: pendingReviewTitleFromArgs,
    enabled: isSignalUpdateReview,
  });
  const headerMetadata = useMemo(() => {
    if (!config.extractMetadata || !toolArgs) return undefined;

    return config.extractMetadata({ input: toolArgs });
  }, [config, toolArgs]);
  const guardrailReasonHeaderAction = getGuardrailReasonHeaderAction(toolArgs);
  const isElevatedRequest = Boolean(primaryActionRequest?.elevated);
  const headerActionsNode = withElevatedBadge({
    elevated: isElevatedRequest,
    actions: guardrailReasonHeaderAction,
  });
  const { dispatchDecision, pendingDecision, isPendingRef } =
    useHitlDecisionDispatch({
      dataMessage,
      messagePartId,
      isActionable: canDecide,
      onHITLActionMessageEvent,
      onCloseEditInMainContent,
    });
  const dispatchDecisionRef = useRef(dispatchDecision);

  useEffect(() => {
    dispatchDecisionRef.current = dispatchDecision;
  }, [dispatchDecision]);
  const editOption = findOptionByValue(hitlRequest?.options ?? [], 'edit');
  const handleEditComplete = useCallback(
    (editedArgs: Record<string, unknown>) => {
      if (!editOption || !primaryActionRequest) return;
      dispatchDecisionRef.current({
        decision: {
          type: 'edit',
          editedAction: { name: primaryActionRequest.name, args: editedArgs },
        },
        option: editOption,
      });
      setIsEditModalOpen(false);
    },
    [editOption, primaryActionRequest],
  );
  const submitReject = useCallback(
    (message: string) => {
      const rejectOpt = findOptionByValue(hitlRequest?.options ?? [], 'reject');

      if (!rejectOpt) return;
      if (isMultiAction) {
        const decisions = actionRequests.map((actionRequest) =>
          buildBatchDecision({
            actionRequest,
            type: 'reject',
            message: message.trim() || undefined,
          }),
        );
        const selectedDecision = decisions[0];

        if (!selectedDecision) return;
        dispatchDecision({
          decision: selectedDecision,
          option: rejectOpt,
          decisions,
        });
        setIsRejectInline(false);
        setRejectMessage('');

        return;
      }

      dispatchDecision({
        decision: primaryActionRequest
          ? buildBatchDecision({
              actionRequest: primaryActionRequest,
              type: 'reject',
              message: message.trim() || undefined,
            })
          : { type: 'reject', message: message.trim() || undefined },
        option: rejectOpt,
      });
      setIsRejectInline(false);
      setRejectMessage('');
    },
    [
      actionRequests,
      hitlRequest?.options,
      isMultiAction,
      primaryActionRequest,
      dispatchDecision,
    ],
  );
  const handleRejectSubmit = useCallback(
    () => submitReject(rejectMessage),
    [submitReject, rejectMessage],
  );
  const handleRejectSkip = useCallback(() => submitReject(''), [submitReject]);
  const handleRejectCancel = useCallback(() => {
    setIsRejectInline(false);
    setRejectMessage('');
  }, []);
  const handleRejectKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleRejectSubmit();
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        handleRejectCancel();
      }
    },
    [handleRejectSubmit, handleRejectCancel],
  );

  if (!hitlRequest) return null;
  const effectiveDecision =
    selectedDecision ?? pendingDecision ?? dataMessage?.decision;
  // For HTTP HITLs, once the tool actually executes, the standalone
  // "Sent HTTP request" card renders the full request + response (and the
  // "user approved" indicator). The HITL prompt card here would otherwise
  // remain in history showing only the frozen request preview, which is
  // a redundant duplicate. Hide it. Rejection is kept as-is because the
  // tool never executes and no standalone card is emitted.
  const hasResolvedToolResultFromCatalog = actionRequests.some(
    (actionRequest) => {
      const actionId = actionRequest.toolCallId ?? actionRequest.id;

      return Boolean(
        actionId && isExecutedCatalogToolResult(toolResultsById?.[actionId]),
      );
    },
  );
  const resolvedHasToolResult =
    hasToolResult || hasResolvedToolResultFromCatalog;
  const rendersOwnStandaloneToolCard =
    isStandaloneResolvedHITLCard(config) &&
    !isPortalReviewContentType(config.contentType);
  const shouldHideResolvedHitlCard =
    !!effectiveDecision &&
    effectiveDecision.type !== 'reject' &&
    !!resolvedHasToolResult &&
    (shouldHideResolvedHitlWhenToolResultExists({
      contentType: config.contentType,
      toolName,
    }) ||
      (rendersOwnStandaloneToolCard && hasToolResult));
  const shouldHideStalePendingHitlCard =
    !effectiveDecision &&
    ((rendersOwnStandaloneToolCard && hasResolvedToolResultFromCatalog) ||
      hasExecutedResultForActions({ actionRequests, toolResultsById }));

  if (shouldHideResolvedHitlCard || shouldHideStalePendingHitlCard) {
    return null;
  }

  const hasContent = isMultiAction
    ? actionRequests.length > 0
    : hitlConfig
      ? extractedData.length > 0
      : !!toolArgs;
  const isReviewablePortalObject = isPortalReviewContentType(
    config.contentType,
  );
  const pendingReviewTitle =
    pendingReviewTitleFromArgs ??
    (isSignalUpdateReview ? resolvedUpdateSignalTitle : undefined);
  const sharedPortalReviewCopy = {
    approvedStatusMessage: t('userApprovedStatus'),
    editedStatusMessage: t('userEditedAndApprovedStatus'),
    savedStatusMessage: t('userApprovedStatus'),
    editedSavedStatusMessage: t('userEditedAndApprovedStatus'),
    rejectedStatusMessage: t('userRejectedStatus'),
    iconColor: 'fg.vulnerability.vulnerable',
    iconBorderColor: 'border.vulnerability.critical',
    iconBg: 'bg.vulnerability.vulnerable',
  };
  const pendingReviewCopy =
    config.contentType === 'signal'
      ? isSignalUpdateReview
        ? {
            ...sharedPortalReviewCopy,
            statusMessage: t('signalUpdateReviewStatus'),
            fallbackTitle: t('signalUpdateReviewFallbackTitle'),
            portalVisibilityMessage: undefined,
            icon: <Target03Icon size={18} />,
          }
        : {
            ...sharedPortalReviewCopy,
            statusMessage: t('signalReviewStatus'),
            fallbackTitle: t('signalReviewFallbackTitle'),
            portalVisibilityMessage: t('signalReviewPortalVisibility'),
            icon: <Target03Icon size={18} />,
          }
      : {
          ...sharedPortalReviewCopy,
          statusMessage: t('findingReviewStatus'),
          fallbackTitle: t('findingReviewFallbackTitle'),
          portalVisibilityMessage: t('findingReviewPortalVisibility'),
          icon: <Alert01Icon size={18} />,
        };
  const renderInlineToolResultContent = ({
    actionRequest,
    hitlDecisionType,
    decision,
  }: {
    actionRequest: HITLActionRequest;
    hitlDecisionType: ResolvedHITLRequestConfirmationDecision['type'];
    decision?: HITLDecision;
    maxOutputHeight: string;
  }) =>
    renderInlineToolResultContentBase({
      actionRequest,
      hitlDecisionType,
      decision,
      toolResultsById,
      getBackgroundExecutionByToolCallId,
      onOpenObject,
    });

  const renderInlineActionRequestContent = (
    params: Omit<
      Parameters<typeof renderInlineActionRequestContentBase>[0],
      'onOpenObject' | 'getBackgroundExecutionByToolCallId'
    >,
  ) =>
    renderInlineActionRequestContentBase({
      ...params,
      onOpenObject,
      getBackgroundExecutionByToolCallId,
    });

  if (effectiveDecision) {
    const title =
      typeof toolArgs?.title === 'string' ? toolArgs.title : undefined;

    if (isMultiAction) {
      const getResolvedActionDecision = (
        actionRequest: HITLActionRequest,
        index: number,
      ) => {
        const actionKey = getActionRequestKey(actionRequest, index);
        const actionId = actionRequest.toolCallId ?? actionRequest.id;

        return (
          multiActionDecisions[actionKey] ??
          selectedDecisions?.find(
            (decision) => (decision.toolCallId ?? decision.id) === actionId,
          )
        );
      };

      let hasNestedWrapperContent = false;
      const actionRows = actionRequests.map((actionRequest, index) => {
        const actionConfig = getToolConfig(actionRequest.name);
        const actionIcon = createElement(actionConfig.icon, { size: 12 });
        const renderer = actionConfig.renderContent ?? defaultContentRenderer;
        const actionDecision = getResolvedActionDecision(actionRequest, index);
        const actionArgs =
          actionRequest.args &&
          typeof actionRequest.args === 'object' &&
          !Array.isArray(actionRequest.args)
            ? (actionRequest.args as Record<string, unknown>)
            : undefined;
        const actionExtractedData =
          actionArgs && actionConfig.hitlConfig?.extractData
            ? actionConfig.hitlConfig.extractData(actionArgs)
            : [];
        const rendered = renderer({
          args: actionArgs,
          extractedData: actionExtractedData as Array<FindingData | SignalData>,
          maxHeight: '160px',
          isEditing: false,
          onSave: () => undefined,
          onCancel: () => undefined,
        });
        const inlineResultContent = renderInlineToolResultContent({
          actionRequest,
          hitlDecisionType: (actionDecision ?? effectiveDecision).type,
          decision: actionDecision ?? effectiveDecision,
          maxOutputHeight: '200px',
        });
        const inlineActionRequestContent = renderInlineActionRequestContent({
          actionRequest,
          hitlDecisionType: (actionDecision ?? effectiveDecision).type,
          decision: actionDecision ?? effectiveDecision,
        });
        const standaloneContent =
          inlineResultContent ?? inlineActionRequestContent;

        if (standaloneContent && isStandaloneResolvedHITLCard(actionConfig)) {
          return (
            <Fragment key={getActionRequestKey(actionRequest, index)}>
              {standaloneContent}
            </Fragment>
          );
        }

        hasNestedWrapperContent = true;

        const resolvedActionDecision = actionDecision ?? effectiveDecision;
        const actionId = actionRequest.toolCallId ?? actionRequest.id;
        const actionToolResult = actionId
          ? toolResultsById?.[actionId]
          : undefined;
        const actionFailureMessage =
          resolvedActionDecision.type !== 'reject' &&
          isPortalReviewContentType(actionConfig.contentType)
            ? readHitlToolFailureMessage(actionToolResult)
            : undefined;
        const actionCreatedResult =
          !actionFailureMessage &&
          actionToolResult?.status === 'completed' &&
          isPortalReviewContentType(actionConfig.contentType)
            ? readPortalObjectResult({
                input:
                  readEditedActionArgs(resolvedActionDecision) ?? actionArgs,
                output: actionToolResult.output,
                contentType: actionConfig.contentType,
                isCompleted: true,
              })
            : null;
        const actionOutcomeRow =
          actionFailureMessage || actionCreatedResult ? (
            <HitlActionOutcomeRow
              failureMessage={actionFailureMessage}
              createdResult={actionCreatedResult}
              onOpenObject={onOpenObject}
            />
          ) : null;

        return (
          <MinimalToolCard
            key={getActionRequestKey(actionRequest, index)}
            icon={actionIcon}
            toolName={actionConfig.name}
            status={getResolvedDecisionCardStatus({
              decision: resolvedActionDecision,
              hasToolResult,
              isLastMessage,
              isChatLoading,
            })}
            hitlStatus={resolvedActionDecision.type}
            defaultExpanded
          >
            <VStack gap={1.5} w="full" alignItems="stretch">
              {actionOutcomeRow}
              {inlineResultContent ?? inlineActionRequestContent ?? rendered}
            </VStack>
          </MinimalToolCard>
        );
      });

      if (!hasNestedWrapperContent) {
        return (
          <VStack gap={2} w="full" alignItems="stretch">
            {actionRows}
          </VStack>
        );
      }

      return (
        <MinimalToolCard
          icon={toolIcon}
          toolName={hitlRequest.title}
          status={getResolvedDecisionCardStatus({
            decision: effectiveDecision,
            hasToolResult,
            isLastMessage,
            isChatLoading,
          })}
          hitlStatus={effectiveDecision.type}
          headerActions={headerActionsNode}
          defaultExpanded
        >
          <VStack gap={2} w="full" alignItems="stretch">
            {actionRows}
          </VStack>
        </MinimalToolCard>
      );
    }

    if (isReviewablePortalObject && !isStandaloneResolvedHITLCard(config)) {
      const editedActionArgs = readEditedActionArgs(effectiveDecision);
      const resolvedReviewTitle =
        readPendingReviewTitle({
          toolArgs: editedActionArgs ?? toolArgs,
          extractedData: editedActionArgs ? [editedActionArgs] : extractedData,
        }) ?? (isSignalUpdateReview ? resolvedUpdateSignalTitle : undefined);
      const actionId =
        primaryActionRequest?.toolCallId ?? primaryActionRequest?.id;
      const toolResult = actionId ? toolResultsById?.[actionId] : undefined;
      const failureMessage =
        effectiveDecision.type !== 'reject'
          ? readHitlToolFailureMessage(toolResult)
          : undefined;
      const portalObjectLookupInput = editedActionArgs ?? toolArgs;
      const createdPortalObjectResult =
        !failureMessage &&
        hasToolResult &&
        effectiveDecision.type !== 'reject' &&
        actionId &&
        config.contentType &&
        toolResult
          ? (readPortalObjectResult({
              input: portalObjectLookupInput,
              output: toolResult.output,
              contentType: config.contentType,
              isCompleted: true,
            }) ??
            (typeof toolResult.message === 'string'
              ? readPortalObjectResult({
                  input: portalObjectLookupInput,
                  output: toolResult.message,
                  contentType: config.contentType,
                  isCompleted: true,
                })
              : null))
          : null;
      const handleOpenCreatedPortalObject =
        createdPortalObjectResult && onOpenObject
          ? () => {
              onOpenObject({
                type: createdPortalObjectResult.objectType,
                id: createdPortalObjectResult.objectId,
                label:
                  createdPortalObjectResult.title ??
                  resolvedReviewTitle ??
                  pendingReviewCopy.fallbackTitle,
              });
            }
          : undefined;
      const resolvedStatusMessage = failureMessage
        ? t('reviewFailedStatus', { message: failureMessage })
        : effectiveDecision.type === 'reject'
          ? pendingReviewCopy.rejectedStatusMessage
          : hasToolResult
            ? effectiveDecision.type === 'edit'
              ? pendingReviewCopy.editedSavedStatusMessage
              : pendingReviewCopy.savedStatusMessage
            : effectiveDecision.type === 'edit'
              ? pendingReviewCopy.editedStatusMessage
              : pendingReviewCopy.approvedStatusMessage;
      const statusIcon =
        effectiveDecision.type === 'reject' || failureMessage ? (
          <CancelCircleIcon size={16} />
        ) : undefined;

      return (
        <MinimalToolCard
          icon={toolIcon}
          toolName={config.name}
          status={getResolvedDecisionCardStatus({
            decision: effectiveDecision,
            hasToolResult,
            isLastMessage,
            isChatLoading,
          })}
          hitlStatus={effectiveDecision.type}
          headerActions={headerActionsNode}
          defaultExpanded
        >
          <PendingObjectReviewCard
            title={resolvedReviewTitle}
            fallbackTitle={pendingReviewCopy.fallbackTitle}
            statusMessage={resolvedStatusMessage}
            statusIcon={statusIcon}
            statusIconColor={
              effectiveDecision.type === 'reject' || failureMessage
                ? 'fg.error'
                : undefined
            }
            icon={pendingReviewCopy.icon}
            iconColor={pendingReviewCopy.iconColor}
            iconBorderColor={pendingReviewCopy.iconBorderColor}
            iconBg={pendingReviewCopy.iconBg}
            onClick={handleOpenCreatedPortalObject}
          />
        </MinimalToolCard>
      );
    }

    const renderApprovedContent = () => {
      if (primaryActionRequest) {
        const inlineResultContent = renderInlineToolResultContent({
          actionRequest: primaryActionRequest,
          hitlDecisionType: effectiveDecision.type,
          decision: effectiveDecision,
          maxOutputHeight: '200px',
        });

        if (inlineResultContent) return inlineResultContent;
        const inlineActionRequestContent = renderInlineActionRequestContent({
          actionRequest: primaryActionRequest,
          hitlDecisionType: effectiveDecision.type,
          decision: effectiveDecision,
        });

        if (inlineActionRequestContent) return inlineActionRequestContent;
      }

      const renderer = config.renderContent ?? defaultContentRenderer;
      const rendered = renderer({
        args: toolArgs,
        extractedData: extractedData as Array<FindingData | SignalData>,
        maxHeight: '200px',
        isEditing: false,
        onSave: () => undefined,
        onCancel: () => undefined,
      });

      if (rendered !== null) return rendered;
      if (title) {
        return (
          <VStack gap={0.5} alignItems="start" py={1}>
            <MetaDefault color="fg.muted">{t('suggestedFinding')}</MetaDefault>
            <BodyStrong>{title}</BodyStrong>
          </VStack>
        );
      }

      return null;
    };

    const approvedContent = renderApprovedContent();

    if (approvedContent && isStandaloneResolvedHITLCard(config)) {
      return approvedContent;
    }

    return (
      <MinimalToolCard
        icon={toolIcon}
        toolName={config.name}
        status={getResolvedDecisionCardStatus({
          decision: effectiveDecision,
          hasToolResult,
          isLastMessage,
          isChatLoading,
        })}
        hitlStatus={effectiveDecision.type}
        metadata={headerMetadata}
        headerActions={headerActionsNode}
        defaultExpanded={hasContent}
      >
        {hasContent ? approvedContent : undefined}
      </MinimalToolCard>
    );
  }

  if (!canDecide) return null;
  const approveOption = findOptionByValue(hitlRequest.options, 'approve');
  const rejectOption = findOptionByValue(hitlRequest.options, 'reject');
  const maybeDispatchMultiActionDecisions = ({
    decisionsByKey,
    selectedOption,
  }: {
    decisionsByKey: Record<string, HITLDecision>;
    selectedOption: PromptOption;
  }) => {
    const decisions = actionRequests
      .map((actionRequest, index) => {
        const key = getActionRequestKey(actionRequest, index);

        return decisionsByKey[key];
      })
      .filter((decision): decision is HITLDecision => !!decision);

    if (decisions.length !== actionRequests.length) return;
    const hasRejectedDecision = decisions.some(
      (decision) => decision.type === 'reject',
    );
    const selectedDecision = hasRejectedDecision
      ? decisions.find((decision) => decision.type === 'reject')
      : decisions[0];

    if (!selectedDecision) return;
    const aggregateOption =
      hasRejectedDecision && rejectOption ? rejectOption : selectedOption;

    dispatchDecision({
      decision: selectedDecision,
      option: aggregateOption,
      decisions,
    });
  };

  const handleMultiActionApprove = (
    actionRequest: HITLActionRequest,
    index: number,
  ) => {
    if (!approveOption) return;
    const key = getActionRequestKey(actionRequest, index);
    const nextDecisions = {
      ...multiActionDecisions,
      [key]: buildBatchDecision({ actionRequest, type: 'approve' }),
    };

    setMultiActionDecisions(nextDecisions);
    if (rejectingActionKey === key) {
      setRejectingActionKey(null);
      setMultiRejectMessage('');
    }

    maybeDispatchMultiActionDecisions({
      decisionsByKey: nextDecisions,
      selectedOption:
        nextDecisions[key]?.type === 'reject' && rejectOption
          ? rejectOption
          : approveOption,
    });
  };

  const handleMultiActionRejectStart = (
    actionRequest: HITLActionRequest,
    index: number,
  ) => {
    if (!rejectOption) return;
    setRejectingActionKey(getActionRequestKey(actionRequest, index));
    setMultiRejectMessage('');
  };

  const handleMultiActionRejectSubmit = (
    actionRequest: HITLActionRequest,
    index: number,
    message: string,
  ) => {
    if (!rejectOption) return;
    const key = getActionRequestKey(actionRequest, index);
    const nextDecisions = {
      ...multiActionDecisions,
      [key]: buildBatchDecision({
        actionRequest,
        type: 'reject',
        message: message.trim() || undefined,
      }),
    };

    setMultiActionDecisions(nextDecisions);
    setRejectingActionKey(null);
    setMultiRejectMessage('');
    maybeDispatchMultiActionDecisions({
      decisionsByKey: nextDecisions,
      selectedOption: rejectOption,
    });
  };

  const handleMultiActionEditComplete = (
    actionRequest: HITLActionRequest,
    index: number,
    editedArgs: Record<string, unknown>,
  ) => {
    const optionForEdit = editOption ?? approveOption;

    if (!optionForEdit) return;
    const key = getActionRequestKey(actionRequest, index);
    const nextDecisions: Record<string, HITLDecision> = {
      ...multiActionDecisions,
      [key]: {
        type: 'edit',
        editedAction: { name: actionRequest.name, args: editedArgs },
        ...getActionRequestDecisionIds(actionRequest),
      },
    };

    setMultiActionDecisions(nextDecisions);
    if (rejectingActionKey === key) {
      setRejectingActionKey(null);
      setMultiRejectMessage('');
    }

    maybeDispatchMultiActionDecisions({
      decisionsByKey: nextDecisions,
      selectedOption: optionForEdit,
    });
  };

  const handleApprove = () => {
    if (!approveOption) return;
    if (isMultiAction) {
      const decisions = actionRequests.map((actionRequest) =>
        buildBatchDecision({ actionRequest, type: 'approve' }),
      );
      const selectedDecision = decisions[0];

      if (!selectedDecision) return;
      dispatchDecision({
        decision: selectedDecision,
        option: approveOption,
        decisions,
      });

      return;
    }

    dispatchDecision({
      decision: primaryActionRequest
        ? buildBatchDecision({
            actionRequest: primaryActionRequest,
            type: 'approve',
          })
        : { type: 'approve' },
      option: approveOption,
    });
  };

  const handleHttpInlineSave = (editedArgs: Record<string, unknown>) => {
    setIsEditingHttpInline(false);
    handleEditComplete(editedArgs);
  };

  const handleHttpInlineCancel = () => {
    setIsEditingHttpInline(false);
  };

  const openPortalReview = ({
    actionRequest,
    actionIndex,
  }: {
    actionRequest?: HITLActionRequest;
    actionIndex?: number;
  } = {}) => {
    const request = actionRequest ?? primaryActionRequest;

    if (!editOption || !request || !canDecide) return;
    const hitlMessageId = messagePartId ?? dataMessage?.id;

    if (!hitlMessageId) return;
    const reviewArgs =
      request.args &&
      typeof request.args === 'object' &&
      !Array.isArray(request.args)
        ? (request.args as Record<string, unknown>)
        : {};
    const reviewConfig = getToolConfig(request.name);
    const title =
      typeof reviewArgs.title === 'string' ? reviewArgs.title : undefined;
    const isPerActionReview = isMultiAction && actionIndex !== undefined;

    if (onOpenEditInMainContent) {
      onOpenEditInMainContent({
        toolName: request.name,
        args: reviewArgs,
        hitlMessageId: isPerActionReview
          ? `${hitlMessageId}-${actionIndex}`
          : hitlMessageId,
        objectType: reviewConfig.contentType,
        title,
        onEditComplete: isPerActionReview
          ? (editedArgs) =>
              handleMultiActionEditComplete(request, actionIndex, editedArgs)
          : handleEditComplete,
        onApprove: isPerActionReview
          ? () => handleMultiActionApprove(request, actionIndex)
          : handleApprove,
        onReject: isPerActionReview
          ? (message?: string) =>
              handleMultiActionRejectSubmit(request, actionIndex, message ?? '')
          : (message?: string) => submitReject(message ?? ''),
      });

      return;
    }

    setIsEditModalOpen(true);
  };

  const handleEdit = () => {
    if (!editOption || !primaryActionRequest || !canDecide) return;
    if (config.usesInlineEdit) {
      setIsEditingHttpInline(true);

      return;
    }

    if (config.usesTabEdit) {
      openPortalReview();

      return;
    }

    setIsEditModalOpen(true);
  };

  const handleRejectAction = () => {
    if (isPendingRef.current || !rejectOption || !dataMessage || !canDecide)
      return;
    setIsRejectInline(true);
  };

  const handleReviewAndSave = () => {
    openPortalReview();
  };

  const isDisabled =
    !!pendingDecision ||
    !!selectedDecision ||
    !!dataMessage?.decision ||
    !canDecide ||
    isApprovalBlocked;
  const shouldRenderPendingPortalObjectReviewCard =
    !isMultiAction &&
    isReviewablePortalObject &&
    config.usesTabEdit &&
    !!editOption &&
    canDecide &&
    !isStandaloneResolvedHITLCard(config);
  const hasAnyMultiActionDecision =
    Object.keys(multiActionDecisions).length > 0;
  const isBulkApprovalBlocked = blockedActionKeys.size > 0;
  const shouldRenderBulkActionBar =
    isMultiAction &&
    !hasAnyMultiActionDecision &&
    !rejectingActionKey &&
    !isRejectInline &&
    canDecide;
  const renderBulkActionBar = () => {
    if (!shouldRenderBulkActionBar) return null;

    return (
      <HStack
        gap={1}
        alignItems="center"
        justifyContent="flex-end"
        w="full"
        pb={1}
      >
        <MetaDefault color="fg.muted" mr="auto">
          {t('bulkPendingLabel', { count: actionRequests.length })}
        </MetaDefault>
        {rejectOption ? (
          <HitlPendingActionButton
            label={t('rejectAll')}
            icon={<CancelCircleIcon size={12} />}
            onClick={handleRejectAction}
            disabled={isDisabled}
          />
        ) : null}
        {approveOption ? (
          <HitlPendingActionButton
            label={t('approveAllCount', { count: actionRequests.length })}
            icon={<CheckmarkCircle02Icon size={12} />}
            onClick={handleApprove}
            disabled={isDisabled || isBulkApprovalBlocked}
          />
        ) : null}
      </HStack>
    );
  };

  const renderMultiActionContent = () => (
    <VStack gap={2} w="full" alignItems="stretch">
      {renderBulkActionBar()}
      {actionRequests.map((actionRequest, index) => {
        const actionConfig = getToolConfig(actionRequest.name);
        const actionIcon = createElement(actionConfig.icon, { size: 12 });
        const renderer = actionConfig.renderContent ?? defaultContentRenderer;
        const actionKey = getActionRequestKey(actionRequest, index);
        const actionDecision = multiActionDecisions[actionKey];
        const isActionApprovalBlocked = blockedActionKeys.has(actionKey);
        const actionArgs =
          actionRequest.args &&
          typeof actionRequest.args === 'object' &&
          !Array.isArray(actionRequest.args)
            ? (actionRequest.args as Record<string, unknown>)
            : undefined;
        const actionExtractedData =
          actionArgs && actionConfig.hitlConfig?.extractData
            ? actionConfig.hitlConfig.extractData(actionArgs)
            : [];
        const canReviewAction = isPortalReviewContentType(
          actionConfig.contentType,
        );
        const actionPendingSlots = buildPendingApprovalSlots({
          toolConfig: actionConfig,
          request: actionRequest,
          controls: !actionDecision
            ? {
                onApprove: () => handleMultiActionApprove(actionRequest, index),
                onAlwaysAllow: buildAlwaysAllowHandler(actionRequest, () =>
                  handleMultiActionApprove(actionRequest, index),
                ),
                onReject: () =>
                  handleMultiActionRejectStart(actionRequest, index),
                onReview:
                  canReviewAction && editOption
                    ? () =>
                        openPortalReview({ actionRequest, actionIndex: index })
                    : undefined,
                disabled: isDisabled || isActionApprovalBlocked,
              }
            : undefined,
        });
        const artifactRendered = renderInlineActionRequestContent({
          actionRequest,
          headerActions: actionPendingSlots.headerActions,
          pendingApproval: actionPendingSlots.pendingApproval,
          hitlDecisionType: actionDecision?.type,
          onOpenReview:
            canReviewAction && editOption && !actionDecision
              ? () => openPortalReview({ actionRequest, actionIndex: index })
              : undefined,
        });
        const rendered = renderer({
          args: actionArgs,
          extractedData: actionExtractedData as Array<FindingData | SignalData>,
          maxHeight: '160px',
          isEditing: false,
          onSave: () => undefined,
          onCancel: () => undefined,
        });
        const rejectForm =
          rejectingActionKey === actionKey ? (
            <HitlRejectForm
              actionName={actionConfig.name}
              message={multiRejectMessage}
              onMessageChange={setMultiRejectMessage}
              onCancel={() => {
                setRejectingActionKey(null);
                setMultiRejectMessage('');
              }}
              onSkip={() =>
                handleMultiActionRejectSubmit(actionRequest, index, '')
              }
              onSubmit={() =>
                handleMultiActionRejectSubmit(
                  actionRequest,
                  index,
                  multiRejectMessage,
                )
              }
            />
          ) : null;

        if (artifactRendered && isStandaloneResolvedHITLCard(actionConfig)) {
          return (
            <VStack key={actionKey} gap={1.5} w="full" alignItems="stretch">
              {artifactRendered}
              {rejectForm}
            </VStack>
          );
        }

        return (
          <MinimalToolCard
            key={actionKey}
            icon={actionIcon}
            toolName={actionConfig.name}
            status="pending"
            hitlStatus={actionDecision?.type ?? 'pending'}
            onApprove={
              actionDecision
                ? undefined
                : () => handleMultiActionApprove(actionRequest, index)
            }
            onReject={
              actionDecision
                ? undefined
                : () => handleMultiActionRejectStart(actionRequest, index)
            }
            disabled={isDisabled || isActionApprovalBlocked}
            defaultExpanded
          >
            <VStack gap={1.5} w="full" alignItems="stretch">
              {artifactRendered ?? rendered}
              {rejectForm}
            </VStack>
          </MinimalToolCard>
        );
      })}
      {isRejectInline && (
        <HitlRejectForm
          actionName={hitlRequest.title}
          message={rejectMessage}
          onMessageChange={setRejectMessage}
          onCancel={handleRejectCancel}
          onSkip={handleRejectSkip}
          onSubmit={handleRejectSubmit}
          onKeyDown={handleRejectKeyDown}
        />
      )}
    </VStack>
  );
  const renderPendingArtifact = () => {
    if (
      isMultiAction ||
      shouldRenderPendingPortalObjectReviewCard ||
      !primaryActionRequest ||
      isEditingHttpInline
    ) {
      return undefined;
    }

    const pendingSlots = buildPendingApprovalSlots({
      toolConfig: config,
      request: primaryActionRequest,
      controls: {
        onApprove: approveOption ? handleApprove : undefined,
        onAlwaysAllow: approveOption
          ? buildAlwaysAllowHandler(primaryActionRequest, handleApprove)
          : undefined,
        onReject: rejectOption ? () => submitReject('') : undefined,
        onEdit:
          !isReviewablePortalObject && editOption ? handleEdit : undefined,
        onReview:
          isReviewablePortalObject && editOption
            ? handleReviewAndSave
            : undefined,
        disabled: isDisabled,
        enableShortcuts: Boolean(isLastMessage),
      },
    });

    return renderInlineActionRequestContent({
      actionRequest: primaryActionRequest,
      headerActions: pendingSlots.headerActions,
      pendingApproval: pendingSlots.pendingApproval,
      onOpenReview:
        isReviewablePortalObject && editOption
          ? handleReviewAndSave
          : undefined,
    });
  };

  const pendingArtifact = renderPendingArtifact();
  const renderPendingToolContent = () => {
    if (isMultiAction) {
      return renderMultiActionContent();
    }

    if (shouldRenderPendingPortalObjectReviewCard) {
      return (
        <PendingObjectReviewCard
          title={pendingReviewTitle}
          fallbackTitle={pendingReviewCopy.fallbackTitle}
          statusMessage={pendingReviewCopy.statusMessage}
          portalVisibilityMessage={pendingReviewCopy.portalVisibilityMessage}
          icon={pendingReviewCopy.icon}
          iconColor={pendingReviewCopy.iconColor}
          iconBorderColor={pendingReviewCopy.iconBorderColor}
          iconBg={pendingReviewCopy.iconBg}
          onReviewAndSave={handleReviewAndSave}
          onClick={handleReviewAndSave}
          disabled={isDisabled}
        />
      );
    }

    if (pendingArtifact) {
      return pendingArtifact;
    }

    const renderer = config.renderContent ?? defaultContentRenderer;

    return renderer({
      args: toolArgs,
      extractedData: extractedData as Array<FindingData | SignalData>,
      maxHeight: '200px',
      isEditing: isEditingHttpInline,
      onSave: handleHttpInlineSave,
      onCancel: handleHttpInlineCancel,
    });
  };

  const renderEditModal = () => {
    if (!isEditModalOpen || config.usesInlineEdit) return null;
    const renderer = config.renderEditModal ?? defaultEditModalRenderer;

    return renderer({
      isOpen: true,
      onClose: () => setIsEditModalOpen(false),
      onSubmit: handleEditComplete,
      onApprove: handleApprove,
      onReject: (message?: string) => submitReject(message ?? ''),
      toolName,
      args: toolArgs,
      extractedData,
    });
  };

  const pendingToolContent = renderPendingToolContent();
  const areAllMultiActionsStandalone =
    isMultiAction &&
    actionRequests.every((actionRequest) =>
      isStandaloneResolvedHITLCard(getToolConfig(actionRequest.name)),
    );
  const shouldRenderStandalonePendingArtifact =
    !effectiveDecision &&
    !shouldRenderPendingPortalObjectReviewCard &&
    pendingToolContent !== null &&
    ((isStandaloneResolvedHITLCard(config) &&
      !isMultiAction &&
      (Boolean(pendingArtifact) || isEditingHttpInline)) ||
      areAllMultiActionsStandalone);

  if (shouldRenderStandalonePendingArtifact) {
    return (
      <>
        {pendingToolContent}
        {renderEditModal()}
      </>
    );
  }

  return (
    <>
      <MinimalToolCard
        icon={toolIcon}
        toolName={isMultiAction ? hitlRequest.title : config.name}
        status="pending"
        hitlStatus="pending"
        onApprove={
          isMultiAction || shouldRenderPendingPortalObjectReviewCard
            ? undefined
            : handleApprove
        }
        onReject={
          isMultiAction || shouldRenderPendingPortalObjectReviewCard
            ? undefined
            : rejectOption
              ? handleRejectAction
              : undefined
        }
        onEdit={
          shouldRenderPendingPortalObjectReviewCard
            ? undefined
            : editOption
              ? handleEdit
              : undefined
        }
        disabled={isDisabled}
        defaultExpanded={hasContent || isRejectInline}
        forceExpanded={canDecide}
        headerActions={headerActionsNode}
      >
        <VStack gap={3} w="full">
          {pendingToolContent}
          {!shouldRenderPendingPortalObjectReviewCard &&
            config.usesTabEdit &&
            onOpenEditInMainContent &&
            editOption &&
            canDecide && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReviewAndSave}
                disabled={isDisabled}
              >
                {t('reviewAndSave')}
              </Button>
            )}
          {!isMultiAction && isRejectInline && (
            <HitlRejectForm
              actionName={toolName ?? 'Unknown'}
              message={rejectMessage}
              onMessageChange={setRejectMessage}
              onCancel={handleRejectCancel}
              onSkip={handleRejectSkip}
              onSubmit={handleRejectSubmit}
              onKeyDown={handleRejectKeyDown}
            />
          )}
        </VStack>
      </MinimalToolCard>

      {renderEditModal()}
    </>
  );
}
